// AI Settings Byte (ASB) for the `RT511SET...` program message.
// PeeCee ('Ace'), Roboki and Zing ('Humanoid') firmware v2/v3 use it: payload
// index 8 is 'X' and index 9 carries the ASB bitmask. On Zing, indexes 8/9 are
// also the A1/A2 port bytes, so it is only written when an AI feature is
// selected or A1 is already 'X' (the Zing v3 A1/A2 = 'X' override).

export const ASB_DEVICES = ['Ace', 'Roboki']
export const ZING_DEVICE = 'Humanoid'
export const ZING_ASB_MAJOR_VERSIONS = ['2', '3']
export const ASB_MARKER_INDEX = 8
export const ASB_INDEX = 9

// Bit positions per the AI SETTINGS BYTE table.
export const ASB_BITS = {
    LED: 0,
    CAMERA_FACE: 1,
    CAMERA_MOTION: 2,
    SPEECH: 3,
    AUDIO: 4,
    SWITCH: 5,
    LIDAR: 6,
}

// Selected-state keys (assemblyPageAi.aiItems) that switch each feature on.
const FACE_KEYS = [
    'ai_face_detection',
    'ai_face_recognition',
    'ai_face_tracking',
]
const COLOR_KEYS = ['ai_color_recognition', 'ai_color_tracking']
const LIDAR_KEYS = ['ai_lidar', 'ai_proximity', 'ai_light']

// AI selection resolved for the program currently being generated.
let currentAiItems = {}

export function setAiSettingsItems(aiItems) {
    currentAiItems = aiItems || {}
}

export function getAiSettingsItems() {
    return currentAiItems
}

// An item is enabled if any source has it enabled.
export function mergeAiItems(...aiItemsList) {
    const merged = {}
    aiItemsList.forEach((aiItems) => {
        Object.keys(aiItems || {}).forEach((key) => {
            if (aiItems[key]) merged[key] = true
        })
    })
    return merged
}

// Resolves the AI selection for a genCode() request from program nodes only.
export function resolveAiSettingsItems(logicComponents, getProgramAiItems) {
    const programAiItems = {}
    const program = logicComponents?.logic?.program
    if (program && getProgramAiItems) {
        getProgramAiItems(program, programAiItems)
    }
    // return mergeAiItems(logicComponents?.aiItems, programAiItems)
    return programAiItems
}

export function computeAiSettingsByte(aiItems = {}) {
    const items = aiItems || {}
    let settingsByte = 0

    if (items.ai_LED) settingsByte |= 1 << ASB_BITS.LED
    if (FACE_KEYS.some((key) => items[key])) {
        settingsByte |= 1 << ASB_BITS.CAMERA_FACE
    }
    // Camera color uses the face and motion bits together.
    if (COLOR_KEYS.some((key) => items[key])) {
        settingsByte |= (1 << ASB_BITS.CAMERA_FACE) | (1 << ASB_BITS.CAMERA_MOTION)
    }
    if (items.ai_motion) settingsByte |= 1 << ASB_BITS.CAMERA_MOTION
    if (items.ai_mic) settingsByte |= 1 << ASB_BITS.SPEECH
    if (items.ai_audio) settingsByte |= 1 << ASB_BITS.AUDIO
    if (items.ai_switch) settingsByte |= 1 << ASB_BITS.SWITCH
    if (LIDAR_KEYS.some((key) => items[key])) {
        settingsByte |= 1 << ASB_BITS.LIDAR
    }

    return settingsByte
}

// Zing firmware v2 (2.0, 2.1, 2.2) and v3. Other Zing versions keep their
// existing payload.
export function isZingAsbVersion(deviceVersion) {
    const major = String(deviceVersion || '').split('.')[0]
    return ZING_ASB_MAJOR_VERSIONS.includes(major)
}

// Writes 'X' at index 8 and the ASB at index 9 of a `RT511SET...` buffer.
// Indexes 8-9 are also the A1/A2 port config, so they are only overwritten
// when an AI feature is selected. No-op for any other device.
export function applyAiSettingsByte(bufView, device, aiItems, deviceVersion) {
    if (
        !ASB_DEVICES.includes(device) &&
        !(device === ZING_DEVICE && isZingAsbVersion(deviceVersion))
    ) {
        return bufView
    }
    const settingsByte = computeAiSettingsByte(aiItems)
    // AI module on port A: index 9 must hold the ASB even when it is 0
    const aiOnPortA = bufView[ASB_MARKER_INDEX] === 'X'.charCodeAt(0)
    // Nothing selected and no AI module: leave the A1/A2 port bytes untouched.
    if (settingsByte === 0 && !aiOnPortA) return bufView
    bufView[ASB_MARKER_INDEX] = 'X'.charCodeAt(0)
    bufView[ASB_INDEX] = settingsByte
    return bufView
}
